import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Calendar,
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  Bell,
  Menu,
  User,
  Check,
  BookOpen,
  Users,
  Building2,
  CreditCard,
} from "lucide-react";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { isIOSApp } from "@/utils/platform";
import UpdateNotification, { VersionBadge } from "@/components/UpdateNotification";
import { ReviewPromptDialog } from "@/components/ReviewPromptCard";
import { SwitchAccountButtons } from "@/components/LinkedAccounts";

export default function Layout({ children }) {
  const { user, logout, token, settings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isBasePlan = (settings?.piano || "base") === "base";
  const isDipendente = user?.ruolo === "dipendente";

  const baseNavItems = isDipendente
    ? [
        // For employees, "Home" is the requests page (with the new request button on top).
        // Dipendenti never see the "Impostazioni" page (it's an admin tool).
        { path: "/requests", label: "Home", icon: LayoutDashboard },
        { path: "/calendar", label: "Calendario", icon: Calendar },
        { path: "/instructions", label: "Istruzioni", icon: BookOpen },
      ]
    : [
        { path: "/dashboard", label: "Home", icon: LayoutDashboard },
        { path: "/requests", label: "Richieste", icon: FileText },
        { path: "/calendar", label: "Calendario", icon: Calendar },
        ...(isBasePlan ? [] : [{ path: "/settings", label: "Impostazioni", icon: Settings }]),
        { path: "/instructions", label: "Istruzioni", icon: BookOpen },
      ];

  // Build navigation items based on role
  let navItems = [...baseNavItems];
  
  // Add "Dipendenti" for titolare and amministrativo (hidden on Base plan)
  if ((user?.ruolo === "titolare" || user?.ruolo === "amministrativo") && !isBasePlan) {
    navItems = [
      ...baseNavItems.slice(0, 2),
      { path: "/users", label: "Dipendenti", icon: Users },
      ...(settings?.reparti_enabled ? [{ path: "/reparti", label: "Reparti", icon: Building2 }] : []),
      ...baseNavItems.slice(2)
    ];
  }
  
  // On Base plan, titolare gets a simplified "Elenco dipendenti" entry
  if (user?.ruolo === "titolare" && isBasePlan) {
    navItems = [
      ...baseNavItems.slice(0, 2),
      { path: "/dipendenti", label: "Elenco dipendenti", icon: Users },
      ...baseNavItems.slice(2)
    ];
  }
  
  // Add "Abbonamento" only for titolare — and NEVER on iOS (Apple Guideline 3.1.1).
  if (user?.ruolo === "titolare" && !isIOSApp()) {
    if (isBasePlan) {
      // On Base plan, append Abbonamento at the end (Settings is hidden)
      navItems.push({ path: "/abbonamento", label: "Abbonamento", icon: CreditCard });
    } else {
      const settingsIndex = navItems.findIndex(item => item.path === "/settings");
      navItems.splice(settingsIndex + 1, 0, { path: "/abbonamento", label: "Abbonamento", icon: CreditCard });
    }
  }

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNotifications(notifRes.data.slice(0, 10));
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error("Failed to fetch notifications");
    }
  };

  const markAsRead = async (id) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, letto: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      console.error("Failed to mark notification as read");
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, letto: true })));
      setUnreadCount(0);
      await axios.put(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      console.error("Failed to mark all as read");
      fetchNotifications();
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setMobileOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl sidebar-link transition-all duration-200 ${
          isActive 
            ? "text-white shadow-lg" 
            : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        }`}
        style={isActive ? {
          background: `linear-gradient(to right, var(--nav-active-from), var(--nav-active-to))`,
          boxShadow: `0 10px 15px -3px var(--nav-active-shadow)`
        } : {}}
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <Icon className={`h-5 w-5 ${isActive ? "text-white" : ""}`} />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white border-r border-slate-200 px-4 py-6">
          {/* Logo */}
          <div className="flex items-start gap-3 px-2 mb-8">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="Logo" className="h-16 w-16 object-contain rounded-lg shrink-0" />
            ) : (
              <div className="h-16 w-16 rounded-xl flex items-center justify-center shadow-lg shrink-0" style={{ background: `linear-gradient(to bottom right, var(--theme-500), var(--theme-600))` }}>
                <Calendar className="h-8 w-8 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0 pt-0.5">
              <h2
                className={`text-slate-900 font-bold leading-tight break-words line-clamp-3 ${
                  (settings.nome_azienda || "").length > 24 ? "text-[11px]" : (settings.nome_azienda || "").length > 14 ? "text-xs" : "text-sm"
                }`}
                title={settings.nome_azienda}
              >
                {settings.nome_azienda}
              </h2>
              <p className="text-slate-500 text-xs mt-0.5">Ferie e Permessi</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>

          {/* User info */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <div className="flex items-center gap-3 px-2">
              <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-slate-900 text-sm font-medium truncate">
                  {user?.nome} {user?.cognome}
                </p>
                <p className="text-slate-500 text-xs capitalize">{user?.ruolo}</p>
              </div>
            </div>
            <SwitchAccountButtons />
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full mt-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100 justify-start"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
            {/* Version badge */}
            <div className="mt-3 px-2">
              <VersionBadge className="text-slate-400" />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header border-b border-slate-200 safe-area-top">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo and Company Name - Always visible on mobile */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="Logo" className="h-14 w-14 object-contain rounded-lg shrink-0" />
            ) : (
              <div className="h-14 w-14 rounded-xl flex items-center justify-center shadow-sm shrink-0" style={{ background: `linear-gradient(to bottom right, var(--theme-500), var(--theme-600))` }}>
                <Calendar className="h-7 w-7 text-white" />
              </div>
            )}
            <div className="min-w-0 flex-1 pt-0.5">
              <h2
                className={`font-bold text-slate-900 leading-tight break-words line-clamp-2 ${
                  (settings.nome_azienda || "").length > 24 ? "text-[11px]" : (settings.nome_azienda || "").length > 14 ? "text-xs" : "text-sm"
                }`}
                title={settings.nome_azienda}
              >
                {settings.nome_azienda}
              </h2>
              <p className="text-slate-500 text-xs mt-0.5">Ferie e Permessi</p>
            </div>
          </div>

          {/* Right side: Notifications + Menu Button */}
          <div className="flex items-center gap-1">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative rounded-xl h-12 w-12"
                  style={{ backgroundColor: 'var(--theme-100)' }}
                  data-testid="notifications-btn-mobile"
                >
                  <Bell className="h-6 w-6" style={{ color: 'var(--theme-700)' }} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="font-semibold">Notifiche</span>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
                      Segna tutte come lette
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="px-3 py-6 text-center text-slate-500 text-sm">
                    Nessuna notifica
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <DropdownMenuItem 
                      key={notif.id} 
                      className="flex flex-col items-start gap-1 p-3 cursor-pointer" 
                      onSelect={(e) => { e.preventDefault(); if (!notif.letto) markAsRead(notif.id); }}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className={`font-medium text-sm ${!notif.letto ? "text-slate-900" : "text-slate-500"}`}>
                          {notif.titolo}
                        </span>
                        {!notif.letto && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--theme-500)' }}></div>}
                      </div>
                      <span className="text-xs text-slate-400">{notif.messaggio}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Menu Button */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-xl h-12 w-12"
                  style={{ backgroundColor: 'var(--theme-600)' }}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="h-7 w-7 text-white" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-white p-0 border-l border-slate-200 safe-area-top">
                <div className="flex flex-col h-full px-4 py-6">
                  {/* User info at top of menu */}
                  <div className="flex items-center gap-3 px-2 mb-6 pb-4 border-b border-slate-200">
                    <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-900 text-sm font-medium truncate">
                        {user?.nome} {user?.cognome}
                      </p>
                      <p className="text-slate-500 text-xs capitalize">{user?.ruolo}</p>
                    </div>
                  </div>
                  
                  {/* Navigation */}
                  <nav className="flex-1 space-y-1">
                    {navItems.map((item) => (
                      <NavLink key={item.path} item={item} mobile />
                    ))}
                  </nav>
                  
                  <SwitchAccountButtons onSwitched={() => setMobileOpen(false)} />
                  {/* Logout button */}
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full mt-4 text-slate-600 hover:text-slate-900 hover:bg-slate-100 justify-start"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Esci
                  </Button>
                  {/* Version badge mobile */}
                  <div className="mt-3 px-2">
                    <VersionBadge className="text-slate-400" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block fixed top-0 left-64 right-0 z-40 glass-header border-b border-slate-200">
        <div className="flex items-center justify-end px-6 py-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative rounded-xl h-11 w-11"
                style={{ backgroundColor: 'var(--theme-100)' }}
                data-testid="desktop-notifications-btn"
              >
                <Bell className="h-5 w-5" style={{ color: 'var(--theme-700)' }} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="font-semibold">Notifiche</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
                    Segna tutte come lette
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="px-3 py-6 text-center text-slate-500 text-sm">
                  Nessuna notifica
                </div>
              ) : (
                notifications.map((notif) => (
                  <DropdownMenuItem 
                    key={notif.id} 
                    className="flex flex-col items-start gap-1 p-3 cursor-pointer" 
                    onSelect={(e) => { e.preventDefault(); if (!notif.letto) markAsRead(notif.id); }}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <span className={`font-medium text-sm ${!notif.letto ? "text-slate-900" : "text-slate-500"}`}>
                        {notif.titolo}
                      </span>
                      {!notif.letto && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--theme-500)' }}></div>}
                    </div>
                    <span className="text-xs text-slate-500 line-clamp-2">{notif.messaggio}</span>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-14 min-h-screen safe-main-offset" style={{ background: `linear-gradient(to bottom right, rgb(248, 250, 252), var(--bg-gradient-via), var(--bg-gradient-to))` }}>
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
      
      {/* Subscription limit warning banner */}
      <SubscriptionBanner />

      {/* Pop-up recensione store (5s dopo l'accesso, una volta per sessione) */}
      <ReviewPromptDialog />
      
      {/* Update notification */}
      <UpdateNotification />
    </div>
  );
}
